import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Lab test cart – dual carts for Pathology vs Radiology so categories never mix in one checkout.
 * Item shape: { id, name, sourceType?, fromPrescriptionId?, patientId?, patientName?, ... }
 * selectedLocation / selectedLab / selectedPackage are shared across categories for the active checkout.
 */

const LabTestCartContext = createContext(null);

export const CART_CATEGORY = {
  PATHOLOGY: 'pathology',
  RADIOLOGY: 'radiology',
};

const STORAGE_KEYS = {
  pathology: 'lab_test_cart_pathology',
  radiology: 'lab_test_cart_radiology',
  active: 'lab_test_cart_active_category',
};

function isPathologySourceType(sourceType) {
  return String(sourceType || '').trim().toLowerCase() === 'pathology';
}

export function cartCategoryForItem(item) {
  return isPathologySourceType(item?.sourceType)
    ? CART_CATEGORY.PATHOLOGY
    : CART_CATEGORY.RADIOLOGY;
}

function readSessionJson(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch {
    return fallback;
  }
}

function writeSessionJson(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / private mode
  }
}

function normalizeCategory(value) {
  return value === CART_CATEGORY.RADIOLOGY
    ? CART_CATEGORY.RADIOLOGY
    : CART_CATEGORY.PATHOLOGY;
}

function normalizeItems(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((i) => i && i.id)
    .map((item) => ({ id: item.id, name: item.name || '', ...item }));
}

export function LabTestCartProvider({ children }) {
  const [pathologyItems, setPathologyItems] = useState(() =>
    normalizeItems(readSessionJson(STORAGE_KEYS.pathology, []))
  );
  const [radiologyItems, setRadiologyItems] = useState(() =>
    normalizeItems(readSessionJson(STORAGE_KEYS.radiology, []))
  );
  const [activeCategory, setActiveCategoryState] = useState(() =>
    normalizeCategory(readSessionJson(STORAGE_KEYS.active, CART_CATEGORY.PATHOLOGY))
  );
  const [selectedLocation, setSelectedLocationState] = useState(null);
  const [selectedLab, setSelectedLabState] = useState(null);
  const [selectedPackage, setSelectedPackageState] = useState(null);

  const persistPathology = useCallback((next) => {
    const items = normalizeItems(next);
    writeSessionJson(STORAGE_KEYS.pathology, items);
    setPathologyItems(items);
    return items;
  }, []);

  const persistRadiology = useCallback((next) => {
    const items = normalizeItems(next);
    writeSessionJson(STORAGE_KEYS.radiology, items);
    setRadiologyItems(items);
    return items;
  }, []);

  const setActiveCategory = useCallback((category) => {
    const next = normalizeCategory(category);
    setActiveCategoryState(next);
    writeSessionJson(STORAGE_KEYS.active, next);
  }, []);

  const setSelectedLocation = useCallback((location) => {
    setSelectedLocationState((prev) => {
      const next = typeof location === 'function' ? location(prev) : location;
      return next && typeof next === 'object' ? next : null;
    });
  }, []);
  const setSelectedLab = useCallback((lab) => {
    setSelectedLabState(lab && typeof lab === 'object' ? lab : null);
  }, []);
  const setSelectedPackage = useCallback((pkg) => {
    setSelectedPackageState(pkg && typeof pkg === 'object' ? pkg : null);
  }, []);

  const items =
    activeCategory === CART_CATEGORY.RADIOLOGY ? radiologyItems : pathologyItems;

  const allItems = useMemo(
    () => [...pathologyItems, ...radiologyItems],
    [pathologyItems, radiologyItems]
  );

  const addItem = useCallback((item) => {
    if (!item?.id) return;
    const category = cartCategoryForItem(item);
    const append = (prev) => {
      const idx = prev.findIndex((i) => i.id === item.id);
      if (idx >= 0) {
        const existing = prev[idx];
        const patientId = item.patientId && !existing.patientId ? item.patientId : null;
        const patientName = item.patientName && !existing.patientName ? item.patientName : null;
        const fromPrescriptionId =
          item.fromPrescriptionId && !existing.fromPrescriptionId
            ? item.fromPrescriptionId
            : null;
        if (!patientId && !patientName && !fromPrescriptionId) return prev;
        const next = [...prev];
        next[idx] = {
          ...existing,
          ...(patientId ? { patientId } : {}),
          ...(patientName ? { patientName } : {}),
          ...(fromPrescriptionId ? { fromPrescriptionId } : {}),
        };
        writeSessionJson(
          category === CART_CATEGORY.PATHOLOGY ? STORAGE_KEYS.pathology : STORAGE_KEYS.radiology,
          next
        );
        return next;
      }
      const next = [...prev, { id: item.id, name: item.name || '', ...item }];
      writeSessionJson(
        category === CART_CATEGORY.PATHOLOGY ? STORAGE_KEYS.pathology : STORAGE_KEYS.radiology,
        next
      );
      return next;
    };
    if (category === CART_CATEGORY.PATHOLOGY) {
      setPathologyItems(append);
    } else {
      setRadiologyItems(append);
    }
  }, []);

  const removeItem = useCallback((id) => {
    if (!id) return;
    const strip = (key) => (prev) => {
      if (!prev.some((i) => i.id === id)) return prev;
      const next = prev.filter((i) => i.id !== id);
      writeSessionJson(key, next);
      return next;
    };
    setPathologyItems(strip(STORAGE_KEYS.pathology));
    setRadiologyItems(strip(STORAGE_KEYS.radiology));
  }, []);

  const clearCart = useCallback(
    (category) => {
      const target = category ? normalizeCategory(category) : activeCategory;
      if (target === CART_CATEGORY.PATHOLOGY) {
        persistPathology([]);
      } else {
        persistRadiology([]);
      }
    },
    [activeCategory, persistPathology, persistRadiology]
  );

  const clearActiveCart = useCallback(() => {
    clearCart(activeCategory);
  }, [activeCategory, clearCart]);

  const setCartItems = useCallback(
    (newItems, category) => {
      const target = category ? normalizeCategory(category) : activeCategory;
      const next = normalizeItems(newItems);
      if (target === CART_CATEGORY.PATHOLOGY) {
        persistPathology(next);
      } else {
        persistRadiology(next);
      }
    },
    [activeCategory, persistPathology, persistRadiology]
  );

  const value = {
    items,
    allItems,
    pathologyItems,
    radiologyItems,
    activeCategory,
    setActiveCategory,
    addItem,
    removeItem,
    clearCart,
    clearActiveCart,
    setCartItems,
    count: items.length,
    pathologyCount: pathologyItems.length,
    radiologyCount: radiologyItems.length,
    totalCount: pathologyItems.length + radiologyItems.length,
    selectedLocation,
    setSelectedLocation,
    selectedLab,
    setSelectedLab,
    selectedPackage,
    setSelectedPackage,
  };

  return (
    <LabTestCartContext.Provider value={value}>
      {children}
    </LabTestCartContext.Provider>
  );
}

export function useLabTestCart() {
  const ctx = useContext(LabTestCartContext);
  if (!ctx) throw new Error('useLabTestCart must be used within LabTestCartProvider');
  return ctx;
}
